import RewardedAdManager from './RewardedAdManager';

/**
 * Recovery UI - Matches the new design system
 * Shows when player can watch an ad to recover/continue
 */
export default class RecoveryUI {
    private container: HTMLDivElement | null = null;
    private overlay: HTMLDivElement | null = null;
    private adManager: RewardedAdManager;
    private isVisible: boolean = false;
    private onRecoveryCallback: (() => void) | null = null;

    constructor() {
        this.adManager = new RewardedAdManager();
        this.createUI();
    }

    private createUI(): void {
        // Overlay
        this.overlay = document.createElement('div');
        this.overlay.className = 'golfup-recovery-overlay';
        this.overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            z-index: 10000;
            display: none;
            align-items: center;
            justify-content: center;
            font-family: 'Fredoka', sans-serif;
        `;

        // Main container
        this.container = document.createElement('div');
        this.container.className = 'golfup-recovery-modal';
        this.container.style.cssText = `
            background: #FFFFFF;
            border: 4px solid #000;
            border-radius: 1rem;
            padding: 2rem;
            max-width: 400px;
            width: 90%;
            text-align: center;
            box-shadow: 8px 8px 0px rgba(0, 0, 0, 0.2);
            transform: rotate(-1deg);
        `;

        // Title
        const title = document.createElement('h2');
        title.textContent = 'Need a Second Chance?';
        title.style.cssText = `
            font-size: 1.75rem;
            font-weight: 900;
            color: #000;
            margin: 0 0 1rem 0;
            -webkit-text-stroke: 1.5px #000;
            text-stroke: 1.5px #000;
        `;

        // Description
        const description = document.createElement('p');
        description.textContent = 'Watch a short video to continue playing!';
        description.style.cssText = `
            font-size: 1rem;
            color: #374151;
            margin: 0 0 1.5rem 0;
        `;

        // Button container
        const buttonContainer = document.createElement('div');
        buttonContainer.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        `;

        // Watch Ad button
        const watchAdButton = document.createElement('button');
        watchAdButton.textContent = '🎬 Watch Ad to Continue';
        watchAdButton.className = 'golfup-recovery-button';
        watchAdButton.style.cssText = `
            background: #FFD93D;
            border: 4px solid #000;
            padding: 1rem 2rem;
            border-radius: 0.5rem;
            font-size: 1.125rem;
            font-weight: 700;
            color: #000;
            cursor: pointer;
            font-family: 'Fredoka', sans-serif;
            transition: transform 0.1s;
            box-shadow: 4px 4px 0px rgba(0, 0, 0, 0.2);
        `;

        watchAdButton.addEventListener('mouseenter', () => {
            watchAdButton.style.transform = 'scale(1.05)';
        });
        watchAdButton.addEventListener('mouseleave', () => {
            watchAdButton.style.transform = 'scale(1)';
        });
        watchAdButton.addEventListener('click', () => {
            this.watchAd();
        });

        // Cancel button
        const cancelButton = document.createElement('button');
        cancelButton.textContent = 'Maybe Later';
        cancelButton.style.cssText = `
            background: transparent;
            border: 2px solid #000;
            padding: 0.75rem 1.5rem;
            border-radius: 0.5rem;
            font-size: 1rem;
            font-weight: 600;
            color: #000;
            cursor: pointer;
            font-family: 'Fredoka', sans-serif;
            transition: background 0.2s;
        `;

        cancelButton.addEventListener('mouseenter', () => {
            cancelButton.style.background = '#F3F4F6';
        });
        cancelButton.addEventListener('mouseleave', () => {
            cancelButton.style.background = 'transparent';
        });
        cancelButton.addEventListener('click', () => {
            this.hide();
        });

        // Ad container (hidden, for Google Ad Manager)
        const adContainer = document.createElement('div');
        adContainer.id = 'rewarded-ad-container';
        adContainer.style.cssText = `
            display: none;
        `;

        // Assemble
        buttonContainer.appendChild(watchAdButton);
        buttonContainer.appendChild(cancelButton);
        this.container.appendChild(title);
        this.container.appendChild(description);
        this.container.appendChild(buttonContainer);
        this.overlay.appendChild(this.container);
        this.overlay.appendChild(adContainer);
        document.body.appendChild(this.overlay);

        // Close on overlay click (but not on modal click)
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) {
                this.hide();
            }
        });
    }

    /**
     * Show the recovery UI
     * @param onRecovery - Callback when player successfully watches ad
     */
    public show(onRecovery: () => void): void {
        if (this.isVisible) return;
        
        this.onRecoveryCallback = onRecovery;
        this.isVisible = true;
        if (this.overlay) {
            this.overlay.style.display = 'flex';
        }
    }

    /**
     * Hide the recovery UI
     */
    public hide(): void {
        if (!this.isVisible) return;
        
        this.isVisible = false;
        this.onRecoveryCallback = null;
        if (this.overlay) {
            this.overlay.style.display = 'none';
        }
    }

    /**
     * Watch ad and grant reward
     */
    private watchAd(): void {
        if (!this.adManager.isAvailable()) {
            alert('Ads are not available right now. Please try again later.');
            return;
        }

        // Hide the modal while ad plays
        if (this.container) {
            this.container.style.display = 'none';
        }

        // Show ad
        // TODO: Replace with your actual Google Ad Manager ad unit path
        // Format: '/12345678/rewarded_video' (get this from your Ad Manager account)
        // See REWARDED_ADS_SETUP.md for instructions
        const adUnitPath = '/12345678/rewarded_video'; // Replace with your actual ad unit path from Google Ad Manager

        this.adManager.showRewardedAd(
            adUnitPath,
            () => {
                // Reward granted
                if (this.onRecoveryCallback) {
                    this.onRecoveryCallback();
                }
                this.hide();
            },
            (error) => {
                // Ad failed
                console.error('RewardedAdManager: Ad error', error);
                if (this.container) {
                    this.container.style.display = 'block';
                }
                alert('Failed to load ad. Please try again.');
            }
        );
    }

    /**
     * Clean up
     */
    public destroy(): void {
        this.adManager.destroy();
        if (this.overlay && this.overlay.parentNode) {
            this.overlay.parentNode.removeChild(this.overlay);
        }
        this.container = null;
        this.overlay = null;
    }
}

